import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Switch,
  VStack,
  HStack,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Divider,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useVoiceService } from '../../../hooks/useVoiceService';

interface CreateRoomModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRoomCreated: () => void;
}

export const CreateRoomModal: React.FC<CreateRoomModalProps> = ({
  isOpen,
  onClose,
  onRoomCreated,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 1, // Standard
    settings: {
      max_groups: 10,
      is_public: true,
      allow_guest_groups: false,
      auto_archive_empty_groups: true,
      auto_archive_days: 7,
      max_created_groups_per_user: 3,
      max_owned_groups_per_user: 2,
      default_group_settings: {
        max_members: 20,
        is_public: true,
        allow_guest: false,
        audio_quality: 2, // High
      },
    },
    default_group_name: 'General',
  });

  const [isLoading, setIsLoading] = useState(false);
  const { createRoom } = useVoiceService();
  const toast = useToast();

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({
        title: 'Name is required',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    try {
      setIsLoading(true);
      
      await createRoom(formData);
      
      onRoomCreated();
    } catch (error) {
      // Error is already handled by the service hook
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      type: 1,
      settings: {
        max_groups: 10,
        is_public: true,
        allow_guest_groups: false,
        auto_archive_empty_groups: true,
        auto_archive_days: 7,
        max_created_groups_per_user: 3,
        max_owned_groups_per_user: 2,
        default_group_settings: {
          max_members: 20,
          is_public: true,
          allow_guest: false,
          audio_quality: 2,
        },
      },
      default_group_name: 'General',
    });
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Voice Room</ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Room Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter room name..."
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter room description..."
                rows={3}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Default Group Name</FormLabel>
              <Input
                value={formData.default_group_name}
                onChange={(e) => setFormData(prev => ({ ...prev, default_group_name: e.target.value }))}
                placeholder="Enter default group name..."
              />
            </FormControl>

            <FormControl>
              <FormLabel>Room Type</FormLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: parseInt(e.target.value) }))}
              >
                <option value={1}>Standard</option>
                <option value={2}>Workspace</option>
                <option value={3}>Event</option>
                <option value={4}>Gaming</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Default Group Name</FormLabel>
              <Input
                value={formData.default_group_name}
                onChange={(e) => setFormData(prev => ({ ...prev, default_group_name: e.target.value }))}
                placeholder="General"
              />
            </FormControl>

            <Divider />
            <Text fontWeight="medium">Room Settings</Text>

            <HStack>
              <FormControl>
                <FormLabel>Max Groups</FormLabel>
                <NumberInput
                  value={formData.settings.max_groups}
                  onChange={(_, value) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, max_groups: value || 10 }
                  }))}
                  min={1}
                  max={50}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Auto Archive Days</FormLabel>
                <NumberInput
                  value={formData.settings.auto_archive_days}
                  onChange={(_, value) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, auto_archive_days: value || 7 }
                  }))}
                  min={1}
                  max={365}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </HStack>

            <VStack align="stretch" spacing={3}>
              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="is-public" mb="0">
                  Public Room
                </FormLabel>
                <Switch
                  id="is-public"
                  isChecked={formData.settings.is_public}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, is_public: e.target.checked }
                  }))}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="allow-guest-groups" mb="0">
                  Allow Guest Groups
                </FormLabel>
                <Switch
                  id="allow-guest-groups"
                  isChecked={formData.settings.allow_guest_groups}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, allow_guest_groups: e.target.checked }
                  }))}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="auto-archive" mb="0">
                  Auto Archive Empty Groups
                </FormLabel>
                <Switch
                  id="auto-archive"
                  isChecked={formData.settings.auto_archive_empty_groups}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, auto_archive_empty_groups: e.target.checked }
                  }))}
                />
              </FormControl>
            </VStack>

            <Divider />
            <Text fontWeight="medium">Default Group Settings</Text>

            <HStack>
              <FormControl>
                <FormLabel>Max Members</FormLabel>
                <NumberInput
                  value={formData.settings.default_group_settings.max_members}
                  onChange={(_, value) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      default_group_settings: {
                        ...prev.settings.default_group_settings,
                        max_members: value || 20
                      }
                    }
                  }))}
                  min={1}
                  max={100}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Audio Quality</FormLabel>
                <Select
                  value={formData.settings.default_group_settings.audio_quality}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      default_group_settings: {
                        ...prev.settings.default_group_settings,
                        audio_quality: parseInt(e.target.value)
                      }
                    }
                  }))}
                >
                  <option value={1}>Low</option>
                  <option value={2}>High</option>
                  <option value={3}>Ultra</option>
                </Select>
              </FormControl>
            </HStack>

            <VStack align="stretch" spacing={3}>
              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="default-public" mb="0">
                  Public by Default
                </FormLabel>
                <Switch
                  id="default-public"
                  isChecked={formData.settings.default_group_settings.is_public}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      default_group_settings: {
                        ...prev.settings.default_group_settings,
                        is_public: e.target.checked
                      }
                    }
                  }))}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel htmlFor="default-allow-guest" mb="0">
                  Allow Guests by Default
                </FormLabel>
                <Switch
                  id="default-allow-guest"
                  isChecked={formData.settings.default_group_settings.allow_guest}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      default_group_settings: {
                        ...prev.settings.default_group_settings,
                        allow_guest: e.target.checked
                      }
                    }
                  }))}
                />
              </FormControl>
            </VStack>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Creating..."
          >
            Create Room
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};